import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

interface ShareButtonProps {
  analysisId: string;
  /** Initial privacy state. After publish/copy the local state is owned here. */
  initialIsPublic: boolean;
}

/**
 * Two states:
 *   private → "Share the catch" — publishes (toggle is_public=true) + copies URL.
 *   public  → "Copy link"        — just copies URL.
 *
 * After click → "Copied" / "Shared" confirmation for ~2s, then back to label.
 */
export function ShareButton({ analysisId, initialIsPublic }: ShareButtonProps) {
  const queryClient = useQueryClient();
  const [isPublic, setIsPublic] = useState(initialIsPublic);
  const [transient, setTransient] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Public, server-rendered URL with OG meta. Different from the owner-only
  // SPA route at /c/{id}.
  const shareUrl = `${window.location.origin}/share/${analysisId}`;

  const publish = useMutation({
    mutationFn: () => api.toggleCatchPrivacy(analysisId, true),
    onSuccess: () => {
      setIsPublic(true);
      void copyAndConfirm('Shared — link copied');
      // Public feeds and my-catches need a refresh
      void queryClient.invalidateQueries({ queryKey: ['feed', 'recent'] });
      void queryClient.invalidateQueries({ queryKey: ['feed', 'freshest'] });
      void queryClient.invalidateQueries({ queryKey: ['my-catches'] });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Could not share');
    },
  });

  async function copyAndConfirm(label: string) {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // Clipboard write can fail on insecure contexts. Fallback: show URL.
      // Kept silent — link is still rendered below.
    }
    setTransient(label);
    setTimeout(() => setTransient(null), 2000);
  }

  const handleClick = () => {
    setError(null);
    if (isPublic) {
      void copyAndConfirm('Link copied');
    } else {
      publish.mutate();
    }
  };

  const label = transient
    ? transient
    : publish.isPending
      ? 'Sharing…'
      : isPublic
        ? 'Copy link'
        : 'Share the catch';

  return (
    <div className="flex flex-col items-center gap-2 sm:flex-row sm:items-center">
      <button
        type="button"
        onClick={handleClick}
        disabled={publish.isPending}
        className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-60"
      >
        {label}
      </button>
      {isPublic && (
        <a
          href={shareUrl}
          className="text-xs text-muted-foreground underline hover:text-foreground"
        >
          {shareUrl}
        </a>
      )}
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}
